import type { DynamicAnalyticsData } from '@/entities/bi'
import type { ChartConfig } from '@/shared/components/ui/chart'
import type { CSSProperties } from 'react'

import { useMemo } from 'react'
import { format } from 'date-fns'
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts'

import {
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
} from '@/shared/components/ui/chart'
import { formatNumber, NumberFormats } from '@/shared/tools/locale'

export type PerfomanceSeries = {
	id: string
	name: string
	color?: string
	yAxis?: number
}

export default function PerfomanceChart3({
	chartData,
	series,
}: {
	chartData: DynamicAnalyticsData[]
	series: PerfomanceSeries[]
}) {
	const chartConfig = useMemo(
		() =>
			Object.fromEntries(
				series.map((serie) => [
					serie.id,
					{
						label: serie.name,
						color: serie.color,
					},
				])
			) satisfies ChartConfig,
		[series]
	)

	return (
		<ChartContainer config={chartConfig} className='aspect-[1/0.35]'>
			<LineChart
				style={{
					marginLeft: 0,
					marginRight: 0,
				}}
				accessibilityLayer={false}
				data={chartData}
			>
				<CartesianGrid vertical={false} strokeDasharray='3 3' />
				<XAxis
					dataKey='week_key'
					tickMargin={10}
					axisLine={false}
					tickLine={false}
					fontSize={10}
					tickFormatter={(value) => format(value, 'MMM d')}
				/>
				<YAxis
					yAxisId={0}
					axisLine={false}
					tickLine={false}
					fontSize={10}
					domain={['auto', 'auto']}
					orientation='left'
					width={32}
					tickFormatter={(value) => formatNumber(value, NumberFormats.Compact)}
				/>
				<YAxis
					yAxisId={1}
					axisLine={false}
					tickLine={false}
					fontSize={10}
					domain={['auto', 'auto']}
					orientation='right'
					width={32}
					tickFormatter={(value) => formatNumber(value, NumberFormats.Compact)}
				/>

				<ChartTooltip
					content={
						<ChartTooltipContent
							labelFormatter={(value) =>
								typeof value === 'string' || typeof value === 'number'
									? format(new Date(value), 'dd MMM yyyy')
									: ''
							}
							formatter={(value, name, item) => {
								const config = chartConfig[name as keyof typeof chartConfig]

								return (
									<>
										<div
											className='h-2.5 w-2.5 shrink-0 rounded-[2px] bg-(--color-bg)'
											style={
												{
													'--color-bg': item.color ?? item.fill,
												} as CSSProperties
											}
										/>
										<div className='flex flex-1 items-center justify-between leading-none'>
											<div className='grid gap-1.5'>
												<span className='text-background/50'>
													{config?.label ?? name}
												</span>
											</div>
											{item.value && (
												<span className='ml-1 font-mono font-medium text-background tabular-nums'>
													{name === 'sales_items'
														? formatNumber(
																value as number,
																NumberFormats.Integer
															)
														: formatNumber(
																value as number,
																NumberFormats.Fixed
															)}
												</span>
											)}
										</div>
									</>
								)
							}}
						/>
					}
				/>
				{series.map((serie) => (
					<Line
						key={serie.id}
						dataKey={serie.id}
						type='monotone'
						stroke={serie.color}
						strokeWidth={2}
						dot={{ r: 3, strokeWidth: 1, fill: serie.color }}
						activeDot={{ r: 5 }}
						yAxisId={serie.yAxis}
					/>
				))}
			</LineChart>
		</ChartContainer>
	)
}
